import { VNode } from "snabbdom";
import { Player } from "./hand";
import { Stack } from "./stack";
// --- TYPES ---

export interface Route {
  path: string;
  view: (params: Record<string, string>) => VNode;
}

export interface ClientConfig {
  game: {
    tickrate: number;
    tickrate_ms: number;
    min_move_ticks: number;
    stress_duration_ticks: number;
    stuck_duration_ticks: number;
  };

  network: {
    sync_rate_ticks: number;
  };
  client: {
    min_width: number;
    min_height: number;
    move_duration_ticks: number;
  };
  server: {
    port: number;
  };
}

export enum Suit {
  Spades,
  Clubs,
  Hearts,
  Diamonds,
}
export type Rank = number; // 1 (Ace) to 13 (King)
export type slotIndex = 0 | 1 | 2 | 3 | 8 | 9 | 10 | 11;
export type pileIndex = 5 | 6;
export type deckIndex = 4 | 12;
export type Pile = Stack<number>;

export namespace Card {
  export function rank(val: number): Rank {
    return val % 16;
  }
  export function suit(val: number): Suit {
    if (val < 16) {
      return Suit.Spades;
    }
    if (val < 32) {
      return Suit.Clubs;
    }
    if (val < 48) {
      return Suit.Hearts;
    }
    return Suit.Diamonds;
  }

  export function suitToString(val: number): string {
    switch (Card.suit(val)) {
      case Suit.Spades:
        return "\u2660";
      case Suit.Clubs:
        return "\u2663";
      case Suit.Hearts:
        return "\u2665";
      case Suit.Diamonds:
        return "\u2666";
    }
  }
}

export interface RenderGameState {
  player: Player;
  opponent: Player;
  leftPile: Pile;
  rightPile: Pile;
  sidePile: Pile;
  gamePhase: GamePhase;
  winner: boolean | null; // true player won, false opponent won
  onPileClick: (idx: pileIndex) => void;
  onUncoverClick: (cards: Player) => void;
  onSlotClick: (idx: slotIndex) => void;
  onStressClick: () => void;
}

export interface GameState {
  playerZero: Player;
  playerOne: Player;
  leftPile: Pile;
  rightPile: Pile;
  sidePile: Pile;
  gamePhase: GamePhase;
  winnerId: 0 | 1 | null;
}

export type PlayerState = StateIdle | StateSelected | StatePlaying;
export interface StateIdle {
  kind: "idle";
}
export interface StateSelected {
  kind: "selected";
  slot: slotIndex;
}
export interface StatePlaying {
  kind: "playing";
  card: number;
  srcSlot: slotIndex;
  dstPile: pileIndex;
  startTick: number;
}

export type GamePhase = PhaseNormal | PhaseStuck | PhaseStress;
export interface PhaseNormal {
  kind: "normal";
}
export interface PhaseStuck {
  kind: "stuck";
  startTick: number;
}
export interface PhaseStress {
  kind: "stress";
  startTick: number;
  winnerId: 0 | 1 | undefined;
}

export interface Grid {
  opponentHand: Vec2D;
  opponentDeck: Vec2D;
  leftPile: Vec2D;
  rightPile: Vec2D;
  sidePile: Vec2D;
  playerHand: Vec2D;
  playerDeck: Vec2D;
}

export interface Vec2D {
  x: number;
  y: number;
}

// ================================================
// NETWORK TYPES
// ================================================

export interface GameCreateResponse {
  id: string;
}

export enum Opcode {
  // u8
  Uncover = 0,
  Select = 1,
  MoveStart = 2,
  MoveSuccess = 3,
  MoveFail = 4,
  Stress = 5,
  StressResult = 6,
  Stuck = 7,
  SeedState = 8,
  Synchronize = 9,
  Start = 10,
  Winner = 11,
  EndStress = 12,
  EndStuck = 13,
}

export interface Packet {
  tickAdjustmentMs: number; // i8 lower u24 is reserved 31..0
  tick: number; // u32                                  63..32
  message: Message; // u32 = [4 x u8]                   95..64
}

export type Message =
  | Uncover
  | Select
  | MoveStart
  | MoveSuccess
  | MoveFail
  | Stress
  | StressResult
  | Stuck
  | SeedState
  | Synchronize
  | Start
  | Winner
  | EndStress
  | EndStuck;

export type Uncover = {
  kind: "uncover";
  opcode: Opcode.Uncover;
  srcDeck: deckIndex;
};

export type Select = {
  kind: "select";
  opcode: Opcode.Select;
  slot: slotIndex;
};

export type MoveStart = {
  kind: "moveStart";
  opcode: Opcode.MoveStart;
  srcSlot: slotIndex;
  dstPile: pileIndex;
};

export type MoveSuccess = {
  kind: "moveSuccess";
  opcode: Opcode.MoveSuccess;
  srcSlot: slotIndex;
  dstPile: pileIndex;
};

export type MoveFail = {
  kind: "moveFail";
  opcode: Opcode.MoveFail;
  srcSlot: slotIndex;
  dstPile: pileIndex;
};

export type Stress = {
  kind: "stress";
  opcode: Opcode.Stress;
  playerId: 0 | 1; // u8
};

export type StressResult = {
  kind: "stressResult";
  opcode: Opcode.StressResult;
  winnerId: 0 | 1;
};

export type Stuck = {
  kind: "stuck";
  opcode: Opcode.Stuck;
};

export type SeedState = {
  kind: "seedState";
  opcode: Opcode.SeedState;
  seed: number; // u24 rest of seed is encoded in tickAdjustmentMs (maybe)
};

export type Synchronize = {
  kind: "synchronize";
  opcode: Opcode.Synchronize;
  sequenceNumber: number; // u24
};

export type Start = {
  kind: "start";
  opcode: Opcode.Start;
  playerId: 0 | 1; // u8
};

export type Winner = {
  kind: "winner";
  opcode: Opcode.Winner;
  playerId: 0 | 1; // u24
};

export type EndStress = {
  kind: "endStress";
  opcode: Opcode.EndStress;
};

export type EndStuck = {
  kind: "endStuck";
  opcode: Opcode.EndStuck;
};

// ================================
// ========= TEST TYPES ===========
// ================================

export interface InstructionTest {
  initial: GameState;
  messages: Packet[];
  expected: GameState;
}
